import React, { useEffect, useState, FunctionComponent } from "react";
import {
  hsla,
  styled,
  Block,
  Button,
  ListItemBody,
  ListItemRow,
  ListItemCell,
  ListItemContent,
  List,
  CtaButton,
  Icon,
  IconName,
  CTA_BUTTON_DEFAULTS,
  ColorType,
} from "../../../seenspire-library";
import { IRouted } from "../../routes";
import EmptyTable from "../../components/EmptyTable/EmptyTable";
import { Container } from "../../components/Container/Container";
import {
  Pagination,
  PAGINATION_DEFAULTS,
} from "../../components/Pagination/Pagination";
import { IPaginatedData } from "../../interfaces";
import { useToggle } from "react-use";
import { hasIn, reduce, find, capitalize, get } from "lodash";
import {
  NetworkConnection,
  NetworkConnection5,
  Network,
  IClientApi,
} from "seenspire-api-client";
import { Menu, MenuSection, MenuButton } from "../../components/Menu/Menu";
import ManageSocialNetworkModal from "../../modals/ManageSocialNetworkModal";
import { Layout } from "../../components";
import ConfirmationModal from "../../modals/ConfirmationModal";
import { FeedType, formattedDate, toTime } from "../../util";
import { ReactComponent as EmptySocialNetwork } from "../../../assets/placeholders/emptySocialNetworks.svg";
import Tooltip from "../../components/Tooltip/Tooltip";
import { DeleteButton } from "../AccountUsers/AccountUsers";
import { AppBackgroundColor, WrapOverflow } from "../../styles";
import { useAppContext } from "../../state";

const TableButton = styled(Button)`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  padding: 0 30px;
  min-width: 135px;
  height: 46px;
`;

const StyledCtaButton = styled(CtaButton)`
  width: 200px;

  &:hover {
    width: 200px;
  }
`;

const DarkTableButton = styled(Button)`
  background: #3a414b;
  &:hover {
    background-color: ${hsla("#3A414B", 0.8)};
  }
`;

const ListItemCellFirst = styled(ListItemCell)`
  padding-left: 30px;
`;

const ListItemCellLast = styled(ListItemCell)`
  padding-right: 30px;
`;

const SocialMenuButton = styled(MenuButton)`
  div[name="twitter"] {
    svg {
      width: 20px !important;
      height: 20px !important;
    }
  }
`;

export type SocialNetworkType =
  | "facebook"
  | "twitter"
  | "instagrambusiness"
  | "linkedin"
  | "webex"
  | "workplace"
  | "yammer"
  | "microsoftteams"
  | "slack"
  | "sharepoint"
  | "powerbi";
// | "zoom";

export enum SocialNetworkEnum {
  "facebook" = "facebook",
  "twitter" = "twitter",
  "instagrambusiness" = "instagrambusiness",
  "linkedIn" = "linkedIn",
  "webEx" = "webex",
  "workplace" = "workplace",
  "appSpace" = "appspace",
  "yammer" = "vivaEngage",
  "microsoftteams" = "microsoftteams",
  "slack" = "slack",
  "sharepoint" = "sharepoint",
  "powerbi" = "powerbi",
}

export interface ISocialNetworksTypesConfig {
  id: any;
  uiOrder: number;
  name: SocialNetworkType;
  icon: IconName;
  darkIcon: IconName;
  title: string;
  description: string;
  color: string;
  feedType: FeedType;
  nativeColor: string;
  listItems: string[] | null;
  note: string | null;
  disabled?: boolean;
}

export type SocialNetworkSelectType = {
  label: string;
  value: number;
  icon: string;
};

export type SocialNetworks = {
  [key in SocialNetworkType]: ISocialNetworksTypesConfig;
};

export const SocialNetworkConfig: SocialNetworks = {
  facebook: {
    id: null,
    uiOrder: 1,
    name: "facebook",
    icon: "facebook",
    darkIcon: "facebookDark",
    title: "Facebook",
    description:
      "You need to confirm your connection with your Facebook account. Follow the instructions.",
    feedType: "SOCIAL",
    color: "green",
    nativeColor: "#20649E",
    listItems: ["Posts from public pages"],
    note: "accounts.connect_to_facebook_steps",
  },
  twitter: {
    id: null,
    uiOrder: 2,
    name: "twitter",
    icon: "twitter",
    darkIcon: "twitterDark",
    title: "X",
    description:
      "You need to confirm your connection with your X account. Follow the instructions.",
    feedType: "SOCIAL",
    color: "green",
    nativeColor: "rgb(5, 76, 255)",
    listItems: ["All public posts from a specific account"],
    note: null,
  },
  instagrambusiness: {
    id: null,
    uiOrder: 3,
    name: "instagrambusiness",
    icon: "instagrambusiness",
    darkIcon: "instagrambusinessDark",
    title: "Instagram",
    description:
      "You need to confirm your connection with your Instagram Business account. Follow the instructions.",
    feedType: "SOCIAL",
    color: "green",
    nativeColor: "#FF0076",
    listItems: [
      "All public posts that use a specific hashtag",
      "All posts from your own Instagram account",
      "All posts from verified business accounts",
    ],
    note: null,
  },
  linkedin: {
    id: null,
    uiOrder: 4,
    name: "linkedin",
    icon: "linkedIn",
    darkIcon: "linkedInDark",
    title: "LinkedIn",
    description:
      "You need to confirm your connection with your Linkedin account. Follow the instructions.",
    feedType: "SOCIAL",
    color: "green",
    nativeColor: "#2867B2",
    listItems: [
      "Works only with Company pages",
      "User must be admin of the pages",
    ],
    note: "accounts.connect_to_linkedin_steps",
  },
  workplace: {
    id: null,
    uiOrder: 5,
    name: "workplace",
    icon: "workplace",
    darkIcon: "workplace",
    title: "Workplace",
    description: `You need to confirm your connection with your Workplace account. Follow the instructions.`,
    feedType: "TEAMS",
    color: "green",
    nativeColor: "#0075FF",
    listItems: ["Posts from public pages"],
    note: "You must be assigned a page role for a Facebook page before you can pull videos to your feed.",
    disabled: false,
  },
  slack: {
    id: null,
    uiOrder: 8,
    name: "slack",
    icon: "slack",
    darkIcon: "slack",
    title: "Slack",
    description:
      "You need to confirm your connection with your Slack account. Follow the instructions.",
    feedType: "TEAMS",
    color: "green",
    nativeColor: "#441444",
    listItems: ["All messages retrieved within a selected channel"],
    note: null,
  },
  microsoftteams: {
    id: null,
    uiOrder: 6,
    name: "microsoftteams",
    icon: "microsoftteams",
    darkIcon: "microsoftteamsDark",
    title: "MS Teams",
    description:
      "You need to confirm your connection with your MS Teams account. Follow the instructions.",
    feedType: "TEAMS",
    color: "green",
    nativeColor: "#485BD5",
    listItems: ["All messages retrieved within a selected channel"],
    note: null,
  },
  yammer: {
    id: null,
    uiOrder: 9,
    name: "yammer",
    icon: "vivaEngage",
    darkIcon: "yammerDark",
    title: "Viva Engage",
    description:
      "You need to confirm your connection with your Viva Engage account. Follow the instructions.",
    feedType: "TEAMS",
    color: "green",
    nativeColor: "#1365BE",
    listItems: ["All messages retrieved within a selected channel"],
    note: null,
    disabled: false,
  },
  webex: {
    id: null,
    uiOrder: 10,
    name: "webex",
    icon: "webEx",
    darkIcon: "webEx",
    title: "WebEx",
    description:
      "You need to confirm your connection with your WebEx account. Follow the instructions.",
    feedType: "TEAMS",
    color: "green",
    nativeColor: "#147C4C",
    listItems: ["All messages retrieved by a specific hashtag"],
    note: null,
  },
  sharepoint: {
    id: null,
    uiOrder: 7,
    name: "sharepoint",
    icon: "sharepoint",
    darkIcon: "sharepointDark",
    title: "SharePoint",
    description: "You need to confirm your connection with your SharePoint account. Follow the instructions.",
    feedType: "TEAMS",
    color: "green",
    nativeColor: "#0078D4",
    listItems: ["All messages retrieved within a selected sites"],
    note: null,
  },
  powerbi: {
    id: null,
    uiOrder: 11,
    name: "powerbi",
    icon: "powerbi",
    darkIcon: "powerbiDark",
    title: "PowerBi",
    description:
      "You need to confirm your connection with your PowerBi account. Follow the instructions.",
    feedType: "DASHBOARD",
    color: "green",
    nativeColor: "#0D52F8",
    listItems: ["Retrieve a Dashboard under a specific Workspace"],
    note: null,
  }
  // zoom: {
  //   id: null,
  //   uiOrder: 10,
  //   name: "zoom",
  //   icon: "zoom",
  //   darkIcon: "zoomDark",
  //   title: "Zoom Team Chat",
  //   description:
  //     "You need to confirm your connection with your Zoom Team Chat account. Follow the instructions.",
  //   feedType: "TEAMS",
  //   color: "green",
  //   nativeColor: "#0D52F8",
  //   listItems: ["All messages retrieved by a specific hashtag"],
  //   note: null,
  // },
};

export const getNetworkIcon = (
  connection: any,
  darkMode?: boolean,
  network?: Network
): IconName => {
  const sourceType = connection && connection.availableSourceTypes;

  switch (true) {
    case hasIn(sourceType, ["WEBEXROOM"]) ||
      (network && network.title === SocialNetworkEnum.webEx.toLowerCase()):
      return darkMode ? "webExDark" : "webEx";
    case hasIn(sourceType, ["SHAREPOINTSITE"]):
      return darkMode ? "sharepointDark" : "sharepoint";
    case hasIn(sourceType, ["FACEBOOKPAGE"]) ||
      (network && network.title === SocialNetworkEnum.facebook.toLowerCase()):
      return darkMode ? "facebookDark" : "facebook";
    case hasIn(sourceType, ["LINKEDINPAGE"]) ||
      (network && network.title === SocialNetworkEnum.linkedIn.toLowerCase()):
      return darkMode ? "linkedInDark" : "linkedIn";
    case hasIn(sourceType, ["INSTAGRAMBUSINESSBUSINESS"]) ||
      hasIn(sourceType, ["INSTAGRAMBUSINESSTAG"]) ||
      (network &&
        network.title === SocialNetworkEnum.instagrambusiness.toLowerCase()):
      return darkMode ? "instagrambusinessDark" : "instagrambusiness";
    case hasIn(sourceType, ["TWITTERACCOUNT"]) ||
      hasIn(sourceType, ["TWITTERTAG"]) ||
      (network && network.title === SocialNetworkEnum.twitter.toLowerCase()):
      return darkMode ? "twitterDark" : "twitter";
    case hasIn(sourceType, ["MICROSOFTTEAMSCHANNEL"]) ||
      (network && network.title === SocialNetworkEnum.microsoftteams):
      return darkMode ? "microsoftteamsDark" : "microsoftteams";
    case hasIn(sourceType, ["YAMMERCHANNEL"]) ||
      (network && network.title === SocialNetworkEnum.yammer):
      return darkMode ? "yammerDark" : "yammer";
    case hasIn(sourceType, ["SLACKCHANNEL"]) ||
      (network && network.title === SocialNetworkEnum.slack):
      return darkMode ? "slack" : "slack";
    case hasIn(sourceType, ["POWERBIDASHBOARD"]) ||
      (network && network.title === SocialNetworkEnum.powerbi):
      return "powerbi";
    // case hasIn(sourceType, ["ZOOMROOM"]) ||
    //   (network && network.title === SocialNetworkEnum.zoom):
    //   return darkMode ? "zoomDark" : "zoom";
    default:
      return darkMode ? "workplace" : "workplace";
  }
};

export const enrichNetworks = async (
  SocialNetworkConfig: SocialNetworks,
  client: IClientApi
): Promise<SocialNetworks> => {
  const _socialNetworks = (await client.Open.getNetworks()) as any;

  const enrichedSocialNetworks = reduce(
    SocialNetworkConfig,
    (results, item) => {
      const socialNetwork = find(_socialNetworks.data, {
        title: item.name.toLowerCase(),
      }) as any;
      item.id = socialNetwork && socialNetwork.id;
      results[item.name] = item;
      return results;
    },
    SocialNetworkConfig
  );

  console.log("enrichedSocialNetworks", enrichedSocialNetworks);

  return enrichedSocialNetworks;
};

export const getNetworkFromConnection = (connection: NetworkConnection5) => {
  const networkName = connection.network.title;
  const network = Object.values(SocialNetworkConfig).find(
    (networkConfig) => networkConfig.name === networkName
  );
  return network as ISocialNetworksTypesConfig;
};

enum NetworkConnectionStatusEnum {
  Connected = "Connected",
  Disconnected = "Disconnected",
  Declined = "Declined",
  Requested = "Requested",
}
const generateNetworkStatus = (status: NetworkConnectionStatusEnum) => {
  let color: ColorType = "black";
  switch (status) {
    case NetworkConnectionStatusEnum.Connected:
      color = "success";
      break;
    case NetworkConnectionStatusEnum.Disconnected:
    case NetworkConnectionStatusEnum.Declined:
      color = "alert";
      break;
    case NetworkConnectionStatusEnum.Requested:
      color = "accent";
      break;
  }
  return <Block color={color}>{status}</Block>;
};

const AccountSocialNetworks: FunctionComponent<IRouted> = ({
  t,
  slug,
  setLoading,
  client,
  setTitle,
  route,
  setCtaConfig,
}) => {
  const { twitterEnabled } = useAppContext();

  const [isOpen, toggleModal] = useToggle(false);
  const [selectedNetwork, setSelectedNetwork] =
    useState<ISocialNetworksTypesConfig>(SocialNetworkConfig.facebook);
  const [socialNetworks, setSocialNetworks] = useState(SocialNetworkConfig);
  const [connectionToDelete, setConnectionToDelete] = useState<any>(undefined);
  const [deleteError, setDeleteError] = useState<boolean>(false);
  const [reconnectConnectionId, setReconnectConnectionId] = useState<string>();
  const [data, setData] = useState<IPaginatedData>();
  useEffect(() => {
    setTitle(t("accounts.account_settings_label"));
  }, [setTitle, t]);

  useEffect(() => {
    loadRequiredData();
  }, [client]);

  useEffect(() => {
    loadData(undefined);
  }, [client]);

  const loadData = async (current: number = PAGINATION_DEFAULTS.current) => {
    if (!client) return;
    setLoading(true);
    const results = (await client.NetworkConnections.getConnections(
      100,
      current
    )) as any;

    setData(results);
    setLoading(false);
    setCtaConfig(undefined);
  };

  const loadRequiredData = async () => {
    setLoading(true);
    setSocialNetworks(await enrichNetworks(socialNetworks, client));
    setLoading(false);
  };

  if (!data) return null;

  const connections = data.data
    .filter((network: any) => !["duplicate"].includes(network.status))
    .filter(
      // user_name can lag behind status=connected, so a fresh connection
      // was getting hidden here right after OAuth completed
      (network: any) =>
        network.user_name ||
        network.status === "connected" ||
        network.network.title === SocialNetworkEnum.powerbi
    ) as NetworkConnection[];

  const initiateConnectionModal = (
    network: ISocialNetworksTypesConfig,
    _reconnectConnectionId?: string
  ) => {
    setSelectedNetwork(network);
    setReconnectConnectionId(_reconnectConnectionId);
    toggleModal(true);
  };

  const reconnect = (connection: NetworkConnection) => {
    const network = getNetworkFromConnection(connection);
    initiateConnectionModal(network, String(connection.id));
  };

  const allowTwitter = (name: string) => {
    if (name === "twitter" && twitterEnabled) {
      return true;
    }

    if (name === "twitter" && !twitterEnabled) {
      return false;
    }

    return true;
  };

  return (
    <Layout>
      <AppBackgroundColor />

      <Block display="grid">
        <Menu
          adjustments={{
            ...CTA_BUTTON_DEFAULTS.adjustments,
            right: 0,
            top: -100,
          }}
          menuButton={
            <StyledCtaButton
              keepOpen
              pullUpAmount={216}
              pullRightAmount={0}
              label={t("ADD CONNECTION")}
            />
          }
        >
          <MenuSection>
            {Object.values(SocialNetworkConfig)
              .filter(
                (network) =>
                  network.name !== "workplace" && allowTwitter(network.name)
              )
              .sort((a, b) => a.uiOrder - b.uiOrder)
              .map((network) => {
                return (
                  <SocialMenuButton
                    icon={network.icon}
                    // Uniquify each menu logo's SVG ids: this dropdown renders
                    // every network icon at once and many share generic ids
                    // (e.g. "Page-1", "Combined-Shape", gradient ids), so their
                    // url(#id) refs collided and the logos rendered blank.
                    // See SEE-512.
                    iconProps={{ uniqueIds: true }}
                    key={network.name}
                    onClick={() => initiateConnectionModal(network)}
                  >
                    {network.title}
                  </SocialMenuButton>
                );
              })}
          </MenuSection>
        </Menu>

        <Container overflow="unset" pulledUp skipContentPadding>
          <Block>
            {connections && connections.length === 0 && (
              <EmptyTable
                image={<EmptySocialNetwork />}
                title={t("accounts.network_connections_title")}
                description={t("accounts.network_connections_description")}
              />
            )}

            {connections && connections.length > 0 && (
              <List responsiveAt="small" condense={76}>
                {connections.map((connection, index) => (
                  <ListItemBody key={index}>
                    <ListItemRow>
                      <ListItemCellFirst width="125px">
                        <Block
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <Icon
                            iconSize="medium"
                            name={getNetworkIcon(connection)}
                            // Give each list icon its own SVG ids so the
                            // gradients can't collide with the identical icons
                            // rendered in the "ADD CONNECTION" menu, which made
                            // the logos disappear while the menu was open.
                            // See SEE-512.
                            uniqueIds
                          />
                        </Block>
                      </ListItemCellFirst>
                      <ListItemCell width="225px">
                        <ListItemContent
                          label={`${t("accounts.network_account_name")}:`}
                          description={
                            <>{connection.full_name || "AccountName"}</>
                          }
                        />
                      </ListItemCell>
                      <ListItemCell width="225px">
                        <ListItemContent
                          label={`${t("accounts.network_account_username")}:`}
                          description={
                            <WrapOverflow title={connection.user_name}>
                              {connection.user_name
                                ? connection.user_name
                                : "-"}
                            </WrapOverflow>
                          }
                        />
                      </ListItemCell>
                      <ListItemCell>
                        <ListItemContent
                          label={`${t("accounts.network_account_status")}:`}
                          description={
                            <Block display="flex">
                              {generateNetworkStatus(
                                capitalize(
                                  connection.status
                                ) as NetworkConnectionStatusEnum
                              )}
                              {(connection.status === "disconnected" ||
                                connection.status === "expired" ||
                                connection.status === "requested") && (
                                <Block
                                  padding={{ left: 5 }}
                                  margin={{ top: 2 }}
                                >
                                  <Tooltip
                                    content={t(
                                      "accounts.network_disconnected_tooltip"
                                    )}
                                    icon={
                                      <Icon iconSize="small" name="error" />
                                    }
                                  />
                                </Block>
                              )}
                            </Block>
                          }
                        />
                      </ListItemCell>
                      <ListItemCell>
                        <ListItemContent
                          label={`${t("Last connected on")}:`}
                          description={
                            get(connection, "last_connected")
                              ? `${formattedDate(
                                  get(connection, "last_connected")
                                )} - ${toTime(
                                  get(connection, "last_connected")
                                )}`
                              : "-"
                          }
                        />
                      </ListItemCell>
                      <ListItemCellLast>
                        <Block
                          display="grid"
                          direction="column"
                          gridGap={10}
                          justifyContent="flex-end"
                        >
                          {(connection.status === "declined" ||
                            connection.status === "connected" ||
                            "expired") && (
                            <DeleteButton
                              onClick={() => {
                                setConnectionToDelete(connection.id);
                              }}
                              elementHeightBase={false}
                              elementWidth="free"
                              icon="trashDark"
                              buttonType="outline"
                            />
                          )}
                          {(connection.status === "disconnected" ||
                            connection.status === "expired" ||
                            connection.status === "requested") && (
                            <DarkTableButton
                              color="white"
                              buttonType="outline"
                              icon="refresh"
                              elementHeightBase={false}
                              onClick={() => reconnect(connection)}
                              elementWidth="free"
                            />
                          )}
                        </Block>
                      </ListItemCellLast>
                    </ListItemRow>
                  </ListItemBody>
                ))}
              </List>
            )}
          </Block>
        </Container>
      </Block>

      <Pagination
        meta={data.meta}
        onNavigate={async (page) => await loadData(page)}
      />

      {isOpen && selectedNetwork && (
        <ManageSocialNetworkModal
          selectedNetwork={selectedNetwork}
          isOpen={isOpen}
          reconnectConnectionId={reconnectConnectionId}
          onSubmit={() => {
            toggleModal(false);
            loadData();
          }}
          onCancel={() => {
            toggleModal(false);
            loadData();
          }}
        />
      )}
      <ConfirmationModal
        isOpen={!!connectionToDelete}
        onConfirm={() => {
          if (deleteError) {
            setConnectionToDelete(undefined);
            return;
          }
          return client.NetworkConnections.deleteNetworkConnection(
            connectionToDelete
          )
            .then(() => loadData())
            .then(() => {
              setConnectionToDelete(undefined);
            })
            .catch((error: any) => {
              setDeleteError(true);
            });
        }}
        onCancel={() => {
          setConnectionToDelete(undefined);
          setDeleteError(false);
        }}
        icon={!deleteError ? "deleteSign" : "error"}
        title={" "}
        confirmLabel={!deleteError ? "Delete" : "Got it"}
        subTitle={
          !deleteError
            ? "Delete source connection"
            : "Cannot delete source connection"
        }
        description={
          !deleteError
            ? `You are about to delete this source connection. Are you sure you want to continue?`
            : `This source connection is used by at least one feed and can't be deleted. To delete this source, go to your feed(s) that have the active connection and remove it from the list.`
        }
      />
    </Layout>
  );
};

export { AccountSocialNetworks };
